// src/admin/AdminChats.jsx
import { useState, useEffect, useRef } from "react";
import { subscribeToAllChats, subscribeToMessages, sendMessage, markAsRead } from "../services/chatService";
import { useAuth } from "../context/AuthContext";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

/* ── Layout ── */
.ac-layout {
  display:grid;
  grid-template-columns:300px 1fr;
  gap:14px;
  height:calc(100vh - 56px - 44px);   /* viewport - topbar - page title row */
}

/* ── Conversation list ── */
.ac-list {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:10px; overflow-y:auto; display:flex; flex-direction:column;
}
.ac-list-search {
  padding:10px 12px; border-bottom:1px solid rgba(255,255,255,0.06); flex-shrink:0;
}
.ac-list-search input {
  width:100%; background:rgba(255,255,255,0.05);
  border:1px solid rgba(255,255,255,0.08); border-radius:6px;
  padding:8px 12px; color:#fff; font-family:'Inter',sans-serif;
  font-size:12px; outline:none;
}
.ac-list-search input:focus { border-color:rgba(0,120,242,0.35); }
.ac-list-scroll { flex:1; overflow-y:auto; }

.ac-list-item {
  padding:13px 14px; border-bottom:1px solid rgba(255,255,255,0.04);
  cursor:pointer; transition:background 0.12s; position:relative;
}
.ac-list-item:hover { background:rgba(255,255,255,0.03); }
.ac-list-item.active {
  background:rgba(0,120,242,0.08);
  border-left:2px solid #0078F2;
  padding-left:12px;
}
.ac-list-item-top {
  display:flex; align-items:center; gap:6px; margin-bottom:3px;
}
.ac-list-title {
  font-size:13px; font-weight:700; color:#fff;
  flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
.ac-list-email { font-size:11px; color:#6b6b6b; margin-bottom:3px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ac-list-preview { font-size:11px; color:#4a4a4a; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ac-unread-dot {
  width:7px; height:7px; background:#0078F2; border-radius:50%; flex-shrink:0;
}
.ac-unread-count {
  background:#0078F2; color:#fff; font-size:9px; font-weight:800;
  min-width:16px; height:16px; border-radius:8px;
  display:flex; align-items:center; justify-content:center; padding:0 4px;
  flex-shrink:0;
}

/* ── Chat panel ── */
.ac-panel {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:10px; display:flex; flex-direction:column; overflow:hidden;
  min-height:0;
}
.ac-panel-head {
  padding:13px 16px; border-bottom:1px solid rgba(255,255,255,0.06);
  display:flex; align-items:center; gap:12px; flex-shrink:0;
}
.ac-panel-back {
  display:none; background:none; border:none; cursor:pointer;
  color:#6b6b6b; font-size:18px; padding:2px 6px 2px 0;
  transition:color 0.15s; line-height:1;
}
.ac-panel-back:hover { color:#fff; }
.ac-panel-avatar {
  width:34px; height:34px; background:rgba(255,255,255,0.06);
  border-radius:50%; display:flex; align-items:center; justify-content:center;
  font-size:15px; flex-shrink:0;
}
.ac-panel-info { flex:1; min-width:0; }
.ac-panel-name { font-size:13px; font-weight:700; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ac-panel-sub { font-size:11px; color:#6b6b6b; margin-top:1px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

/* ── Messages ── */
.ac-msgs {
  flex:1; overflow-y:auto; padding:14px 14px;
  display:flex; flex-direction:column; gap:8px;
}
.ac-date-sep {
  text-align:center; font-size:10px; color:#4a4a4a;
  margin:6px 0; font-weight:600; letter-spacing:0.5px;
}
.ac-msg-row { display:flex; gap:8px; max-width:80%; }
.ac-msg-row.mine   { align-self:flex-end;   flex-direction:row-reverse; }
.ac-msg-row.theirs { align-self:flex-start; }
.ac-bubble {
  padding:9px 13px; border-radius:10px;
  font-size:13px; line-height:1.55; font-family:'Inter',sans-serif;
  word-break:break-word;
}
.ac-msg-row.mine   .ac-bubble {
  background:rgba(0,120,242,0.18); border:1px solid rgba(0,120,242,0.28);
  color:#e8f0fe; border-radius:10px 10px 4px 10px;
}
.ac-msg-row.theirs .ac-bubble {
  background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.09);
  color:#c0c0c0; border-radius:10px 10px 10px 4px;
}
.ac-msg-time { font-size:10px; color:rgba(255,255,255,0.25); margin-top:4px; }

.ac-empty {
  flex:1; display:flex; align-items:center; justify-content:center;
  flex-direction:column; gap:12px; color:#6b6b6b;
}
.ac-empty-icon { font-size:44px; opacity:0.5; }
.ac-empty-title { font-size:14px; font-weight:600; color:#a3a3a3; }
.ac-empty-sub { font-size:12px; }

/* ── Input row ── */
.ac-input-row {
  padding:10px 12px; border-top:1px solid rgba(255,255,255,0.06);
  display:flex; gap:8px; align-items:flex-end; flex-shrink:0;
}
.ac-input {
  flex:1; background:rgba(255,255,255,0.05);
  border:1px solid rgba(255,255,255,0.08); border-radius:8px;
  padding:10px 14px; color:#fff; font-family:'Inter',sans-serif;
  font-size:13px; outline:none; transition:border-color 0.15s;
  resize:none; min-height:40px; max-height:100px; line-height:1.5;
}
.ac-input:focus { border-color:rgba(0,120,242,0.4); }
.ac-send-btn {
  width:40px; height:40px; flex-shrink:0;
  background:#0078F2; border:none; border-radius:8px;
  color:#fff; font-size:15px; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  transition:all 0.15s; box-shadow:0 2px 8px rgba(0,120,242,0.3);
}
.ac-send-btn:hover:not(:disabled) { background:#0a84ff; transform:translateY(-1px); }
.ac-send-btn:disabled { opacity:0.3; cursor:not-allowed; transform:none; }

/* ── No chat selected placeholder ── */
.ac-no-select {
  flex:1; display:flex; align-items:center; justify-content:center;
  flex-direction:column; gap:12px; color:#6b6b6b;
}

/* ════════════════════
   MOBILE ≤ 768px
════════════════════ */
@media(max-width:768px) {
  .ac-layout {
    grid-template-columns:1fr;
    height:calc(100dvh - 56px - 44px - 60px - env(safe-area-inset-bottom, 0px));
    gap:0;
  }

  /* On mobile: only show whichever panel is active */
  .ac-layout.show-chat .ac-list  { display:none; }
  .ac-layout.show-list .ac-panel { display:none; }

  .ac-list {
    border-radius:10px;
    height:100%;
  }
  .ac-panel {
    border-radius:10px;
    height:100%;
  }

  /* Show back button on mobile */
  .ac-panel-back { display:flex; align-items:center; }

  /* Bigger tap targets in list */
  .ac-list-item { padding:14px 14px; }
}
`;

export default function AdminChats() {
  const { user } = useAuth();
  const [chats,        setChats]        = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages,     setMessages]     = useState([]);
  const [text,         setText]         = useState("");
  const [sending,      setSending]      = useState(false);
  const [search,       setSearch]       = useState("");
  const bottomRef  = useRef(null);
  const unsubMsgs  = useRef(null);
  const inputRef   = useRef(null);

  useEffect(() => {
    const unsub = subscribeToAllChats(setChats);
    return unsub;
  }, []);

  useEffect(() => {
    if (!selectedChat) return;
    if (unsubMsgs.current) unsubMsgs.current();
    unsubMsgs.current = subscribeToMessages(selectedChat.id, msgs => {
      setMessages(msgs);
      markAsRead(selectedChat.id, "admin");
    });
    return () => { if (unsubMsgs.current) unsubMsgs.current(); };
  }, [selectedChat?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleSend() {
    if (!text.trim() || !selectedChat || sending) return;
    setSending(true);
    try {
      await sendMessage(selectedChat.id, text.trim(), user, "admin");
      setText("");
      inputRef.current?.focus();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  function fmt(ts) {
    if (!ts) return "";
    const d = new Date(typeof ts === "number" ? ts : ts?.toDate?.() ?? ts);
    return d.toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit", hour12:false });
  }

  const filteredChats = search.trim()
    ? chats.filter(c =>
        (c.userEmail||"").toLowerCase().includes(search.toLowerCase()) ||
        (c.gameTitle||"").toLowerCase().includes(search.toLowerCase())
      )
    : chats;

  // Mobile view state: "list" or "chat"
  const mobileView = selectedChat ? "show-chat" : "show-list";

  function selectChat(chat) {
    setSelectedChat(chat);
    setMessages([]);
  }

  return (
    <>
      <style>{S}</style>

      {/* Page title row */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, height:30 }}>
        <div style={{ fontSize:16, fontWeight:800, color:"#fff" }}>💬 Support Chats</div>
        <div style={{ fontSize:12, color:"#6b6b6b", fontWeight:500 }}>
          {chats.filter(c=>c.unreadAdmin>0).length > 0 && (
            <span style={{ background:"rgba(0,120,242,0.15)", border:"1px solid rgba(0,120,242,0.3)", color:"#0078F2", borderRadius:5, padding:"2px 10px", fontWeight:700 }}>
              {chats.filter(c=>c.unreadAdmin>0).length} unread
            </span>
          )}
        </div>
      </div>

      <div className={`ac-layout ${mobileView}`}>

        {/* ── Chat List ── */}
        <div className="ac-list">
          <div className="ac-list-search">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search chats..."
            />
          </div>
          <div className="ac-list-scroll">
            {filteredChats.length === 0 ? (
              <div style={{ padding:"40px 16px", textAlign:"center", color:"#6b6b6b", fontSize:13 }}>
                {search ? "No results" : "No chats yet"}
              </div>
            ) : filteredChats.map(chat => (
              <div
                key={chat.id}
                className={`ac-list-item ${selectedChat?.id === chat.id ? "active" : ""}`}
                onClick={() => selectChat(chat)}
              >
                <div className="ac-list-item-top">
                  <div className="ac-list-title">{chat.gameTitle || "Chat"}</div>
                  {chat.unreadAdmin > 0 && (
                    <div className="ac-unread-count">{chat.unreadAdmin}</div>
                  )}
                </div>
                <div className="ac-list-email">{chat.userEmail}</div>
                <div className="ac-list-preview">{chat.lastMessage || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Chat Panel ── */}
        <div className="ac-panel">
          {!selectedChat ? (
            <div className="ac-no-select">
              <div className="ac-empty-icon">💬</div>
              <div className="ac-empty-title">Select a conversation</div>
              <div className="ac-empty-sub">Choose a chat from the list</div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="ac-panel-head">
                <button className="ac-panel-back" onClick={() => setSelectedChat(null)}>
                  ‹
                </button>
                <div className="ac-panel-avatar">👤</div>
                <div className="ac-panel-info">
                  <div className="ac-panel-name">{selectedChat.userEmail}</div>
                  <div className="ac-panel-sub">
                    {selectedChat.gameTitle} · #{selectedChat.id?.slice(0, 10)}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="ac-msgs">
                {messages.length === 0 && (
                  <div className="ac-empty" style={{ flex:"none", padding:"30px 0" }}>
                    <div style={{ fontSize:28 }}>💬</div>
                    <div style={{ fontSize:12, color:"#6b6b6b" }}>No messages yet</div>
                  </div>
                )}
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`ac-msg-row ${msg.role === "admin" ? "mine" : "theirs"}`}
                  >
                    <div className="ac-bubble">
                      <div>{msg.text}</div>
                      <div className="ac-msg-time">{fmt(msg.timestamp)}</div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="ac-input-row">
                <textarea
                  ref={inputRef}
                  className="ac-input"
                  value={text}
                  rows={1}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Reply to customer… (Enter to send)"
                />
                <button
                  className="ac-send-btn"
                  onClick={handleSend}
                  disabled={!text.trim() || sending}
                >
                  ➤
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}